import { HustleupSmallIcon } from "@images/svgIcons/hustleUpSmallIcon"
import { WarningHexagonalIcon } from "@images/svgIcons/warningIcon"
import { UserProfile, UserProfileEntity } from "@modules/auth/userProfileTypes"
import { word } from "@modules/core/utils/i18n"
import { Path } from "@modules/navigation/routes"
import { useMySpaceNavigate } from "@modules/navigation/useMySpaceNavigate"
import { ProfileAvatar } from "@modules/profile/common/components/profileAvatar"
import { Circles } from "@modules/ui/components/circles"
import { HUText } from "@modules/ui/components/huText"
import styled, { useTheme } from "styled-components"

type ProfileResumeProps = {
  user: UserProfile
}

export const ProfileResume: React.FC<ProfileResumeProps> = ({ user }) => {
  const theme = useTheme()
  const mySpaceNavigate = useMySpaceNavigate()

  const completion = UserProfileEntity.getProfileCompletion(user)
  const isOnlyMissingAvatar = UserProfileEntity.isOnlyMissingAvatar(user)

  const onCompletionClick = () => {
    mySpaceNavigate(Path.MySpace.Profile)
  }

  return (
    <AvatarContainer className="align-items-center justify-content-center gap-4 overflow-hidden px-3">
      {/* Profile avatar */}
      <div className="flex flex-row align-items-center justify-content-center w-full gap-4">
        <ProfileAvatar profile={{ avatar: user.avatar, username: user.username ?? "" }} size={122} />
        <div className="flex flex-column align-items-start overflow-hidden" style={{ maxWidth: "60%" }}>
          <HUText
            fontStyle="HS"
            numberOfLine={2}
            className="inline-block w-full overflow-hidden text-overflow-ellipsis"
          >{`${user.firstname} ${user.lastname}`}</HUText>
          <HUText fontStyle="BM">@{user.username}</HUText>
        </div>
      </div>

      {/* Profile completion ratio */}
      {completion &&
        completion.pcRatio !== 1 &&
        (isOnlyMissingAvatar ? (
          <div
            className="flex flex-row justify-content-center align-items-center gap-2 cursor-pointer"
            onClick={onCompletionClick}
          >
            <WarningHexagonalIcon color={theme.error} size={24} />
            <HUText fontStyle="LS">{word("profile.form.avatar_missing")}</HUText>
          </div>
        ) : (
          <div className="flex flex-column gap-2 cursor-pointer" onClick={onCompletionClick}>
            <Circles count={10} filled={completion.current} color={theme.mainBlack} progressionstyle="line" />
            <HUText fontStyle="LS" className="text-center">
              {word("profile.form.completion", { percentage: Math.ceil(completion.pcRatio * 100) })}
            </HUText>
          </div>
        ))}

      <HustleupSmallIconStyled />
    </AvatarContainer>
  )
}

const AvatarContainer = styled.div`
  position: relative;
  border-radius: 33px;
  border-width: 0px;
  display: flex;
  flex-direction: column;
  align-items: center;
  align-self: stretch;
  background: ${({ theme }) => `linear-gradient(90deg, ${theme.primaryColor} 0%, ${theme.primaryColor600} 100%)`};
  padding: 25px 0px;

  @media screen and (max-width: 1180px) {
    display: none !important;
  }
`

const HustleupSmallIconStyled = styled(HustleupSmallIcon)`
  position: absolute;
  bottom: 0;
  right: -30px;
  height: 100%;
  width: auto;
  opacity: 0.3;
`
